/* eslint-disable @typescript-eslint/no-use-before-define */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { defaultFirebaseClient } from "../../clients/FirebaseClient";
import { ChangeNames, ChangeSet, unwrap } from "../../clients/FirebaseClient/types";
import { RootDispatch, RootState } from "../store";

const EXPECT_SIGN_IN = "expectSignIn";
export const EMAIL_FOR_SIGN_IN = "emailForSignIn";

export type AuthState =
    | {
          loggedIn: true;
          waitingForAuthEvent: boolean;
          isAnonymous: boolean;
          uid: string;
          email?: string | null;
          error?: Error;
      }
    | {
          loggedIn: false;
          uid?: string;
          waitingForAuthEvent: boolean;
          error?: Error;
      };

export const initialAuthState: AuthState = {
    loggedIn: false,
    waitingForAuthEvent: true,
};

const signOut = () => {
    return async (): Promise<void> => {
        await defaultFirebaseClient.unsubscribe();
        await defaultFirebaseClient.signOut();
    };
};

const signInWithMSP = (url?: string) => {
    return async (_dispatch: RootDispatch, getState: () => RootState) => {
        const newConfiguration = getState().configuration.configurations["new"]?.state;
        const powerOrGasAvailable =
            getState().configuration.productPartners?.gas?.foreignProvider === false ||
            getState().configuration.productPartners?.power?.foreignProvider === false;

        const hasNewConfiguration = !!(
            powerOrGasAvailable &&
            newConfiguration?.buildingAddress?.state.city.value.trim() &&
            newConfiguration.buildingAddress.state.zipCode.value.trim()
        );

        const targetUrl = new URL(url || window.location.href);

        if (hasNewConfiguration) {
            const changeSet: Required<ChangeSet<ChangeNames.BUILDING_ADDRESS>> = {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                buildingAddress: unwrap(newConfiguration!.buildingAddress!.state),
            };
            const state = encodeURI(JSON.stringify(changeSet));
            const params = targetUrl.searchParams;
            params.set("state", state);
            targetUrl.search = params.toString();
        }

        const signInUrl = () => {
            const u = new URL(window.location.href);
            u.pathname = "/home/signIn";
            u.searchParams.append("targetUrl", targetUrl.toString());
            return u.toString();
        };

        window.location.assign(signInUrl());
    };
};

const signInWithCustomToken = (token: string) => {
    return async (dispatch: RootDispatch): Promise<void> => {
        dispatch(AuthActions.waitForAuth());
        await defaultFirebaseClient.signInWithCustomToken(token);
    };
};

export const authSlice = createSlice({
    name: "auth",
    initialState: initialAuthState as AuthState,
    reducers: {
        authFailed: (_state, action: PayloadAction<Error>) => {
            window.localStorage.removeItem(EXPECT_SIGN_IN);
            return {
                loggedIn: false,
                waitingForAuthEvent: false,
                error: action.payload,
            };
        },
        authenticated: (
            _state,
            action: PayloadAction<{ isAnonymous: boolean; uid: string; email?: string | null }>,
        ) => {
            window.localStorage.setItem(EXPECT_SIGN_IN, "true");
            return {
                loggedIn: true,
                waitingForAuthEvent: false,
                ...action.payload,
            };
        },
        unauthenticated: () => {
            window.localStorage.removeItem(EXPECT_SIGN_IN);
            return {
                loggedIn: false as false,
                waitingForAuthEvent: false,
            };
        },
        authTimedOut: (state) => {
            window.localStorage.removeItem(EXPECT_SIGN_IN);
            state.waitingForAuthEvent = false;
        },
        waitForAuth: (state) => {
            state.waitingForAuthEvent = true;
        },
    },
});

export const AuthActions = {
    ...authSlice.actions,
    signInWithMSP,
    signInWithCustomToken,
    signOut,
};
